import type { UseScrollConfig } from '../common/helper'
import type { FromWhere } from './declare'
import type { FilterGroup } from 'src/utils/common'
import React, { useState, useMemo, useEffect, useContext } from 'react'
import { ListPage, EditPage, EditTreePage, ChartPage, TreePage, CombinationPage, CustomPage } from 'src/components'
import { appStore } from 'src/stores'
import { $api, $consts } from 'src/plugins'
import { useModalContext } from 'src/components/common/EasyModal'
import { getFormPermission } from './helper'
import { pick } from 'lodash'

type FormData = {
  // 表单本身配置的 formId
  formId?: string,
  // 当前表单父表单配置的 formId（比如说树表单，组合表单）
  parentFormId?: string,
  // 从哪个页面具体被调用
  fromWhere?: FromWhere,
  // 编辑表单的全局禁用
  formDisabled?: boolean,
  // 列表表单 useTableScroll 配置
  useScroll?: UseScrollConfig,
  // 查询参数
  filterGroup?: FilterGroup,
}

type FormProps = {
  formCode: string
  formData?: FormData
}

const FormPropsContext = React.createContext<FormProps>({ formCode: '' })
const FormContext = React.createContext<any>(undefined)

const CheckFormPage = (props: { formState?: any }) => {
  const { formData } = useContext(FormPropsContext)
  const { formState } = props

  const listData = useMemo(() => {
    return formData ? pick(formData, ['fromWhere', 'useScroll', 'filterGroup']) : undefined
  }, [formData])

  const editData = useMemo(() => {
    return formData ? pick(formData, ['formId', 'fromWhere', 'formDisabled']) : undefined
  }, [formData])

  const editTreeData = useMemo(() => {
    return formData ? pick(formData, ['filterGroup']) : undefined
  }, [formData])

  const chartData = useMemo(() => {
    return formData ? pick(formData, ['formId', 'fromWhere', 'filterGroup']) : undefined
  }, [formData])

  const treeData = useMemo(() => {
    return formData ? pick(formData, ['parentFormId', 'fromWhere']) : undefined
  }, [formData])

  const combinationData = useMemo(() => {
    return formData ? pick(formData, ['formId', 'fromWhere', 'filterGroup']) : undefined
  }, [formData])

  const customData = useMemo(() => {
    return formData ? pick(formData, ['parentFormId', 'fromWhere', 'filterGroup']) : undefined
  }, [formData])

  if (!formState || !getFormPermission(formState.ID)) return null

  const { FORM_TYPE, CODE } = formState
  switch (FORM_TYPE) {
    // 列表表单
    case $consts['TABLE/FORM_TYPE_LIST_PAGE']:
      return <ListPage formCode={CODE} formData={listData} />
    // 编辑表单
    case $consts['TABLE/FORM_TYPE_EDIT_PAGE']:
      return <EditPage formCode={CODE} formData={editData} />
    // 编辑树表单
    case $consts['TABLE/FORM_TYPE_EDITTREE_PAGE']:
      return <EditTreePage formCode={CODE} formData={editTreeData} />
    // 图表表单
    case $consts['TABLE/FORM_TYPE_CHART_PAGE']:
      return <ChartPage formCode={CODE} formData={chartData} />
    // 树表单
    case $consts['TABLE/FORM_TYPE_TREE_PAGE']:
      return <TreePage formCode={CODE} formData={treeData} />
    // 组合表单
    case $consts['TABLE/FORM_TYPE_COMBINATION_PAGE']:
      return <CombinationPage formCode={CODE} formData={combinationData} />
    // 可编辑列表表单
    case $consts['TABLE/FORM_TYPE_EDITLIST_PAGE']:
      return <ListPage formCode={CODE} formData={{ __EDIT_V1__: true, ...listData }} />
    // 可编辑动态列列表表单
    case $consts['TABLE/FORM_TYPE_DYNAMIC_EDITLIST_PAGE']:
      return <ListPage formCode={CODE} formData={{ __EDIT_V2__: true, ...listData }} />
    // 自定义表单
    case $consts['TABLE/FORM_TYPE_CUSTOM_PAGE']:
      return <CustomPage formCode={CODE} formData={customData} />
    default:
      return null
  }
}

const FormPage = (props: FormProps) => {
  const modal = useModalContext()

  const [formState, setFormState] = useState()

  useEffect(() => {
    const { OrgId } = appStore.current.headers
    $api['app/getByRedis']({
      Key: props.formCode,
      OrgId
    }).then((res: any) => {
      if (res) {
        setFormState(res)
        // 动态更改 Modal 的标题
        modal.updateModal?.({ title: res.NAME })
      }
    })
  }, [props.formCode])

  return (
    <FormPropsContext.Provider value={props}>
      <FormContext.Provider value={formState}>
        <CheckFormPage formState={formState} />
      </FormContext.Provider>
    </FormPropsContext.Provider>
  )
}

export { FormPropsContext, FormContext }
export default FormPage